import { useCustomEventsQueryContext, useSelectionContext } from 'modules/customEvents/contexts';
import { FIELD_TYPE_FILTER_OPTIONS } from 'modules/dataDictionary/constants';
import { useSearchParamsContext } from 'modules/dataDictionary/contexts';
import { FieldDataType, FieldLockState } from 'modules/dataDictionary/types';
import { DataTypeType } from 'modules/domains';
import { ActionMenuOption, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { Add16 } from 'ui/icons';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { InputSearch } from 'ui/inputs';
import { Menu } from 'ui/menu';
import noop from 'utils/noop';
import { FieldLockNotification } from '../FieldLockNotification';
import {
  Actions,
  FieldLockStateWrapper,
  SearchWrapper,
  TableActionsRow,
  TableActionsWrapper,
} from './TableActions.styled';
import { TableBulkActions } from './TableBulkActions';

export const TABLE_ACTIONS_TEXT = {
  filtersTestId: 'filter-fields',
  inputPlaceholder: 'Search fields',
  newFieldButtonLabel: 'New field',
  newFieldButtonTestId: 'new-field-button',
  searchInputTestId: 'search-fields',
};

export type TableActionsProps = {
  areFiltersEnabled?: boolean;
  /** type of field displayed in the table (ie. 'user', 'purchase') */
  dataType?: DataTypeType;
  fieldType?: FieldDataType;
  onCreateNewField?: (val?: string) => void;
  overflowMenuOptions?: ActionMenuOption[];
  refetchFields?: () => void;
};

export const TableActions = ({
  areFiltersEnabled = true,
  dataType,
  fieldType,
  onCreateNewField,
  overflowMenuOptions,
  refetchFields = noop,
}: TableActionsProps) => {
  const { project } = useCustomEventsQueryContext();
  const { search, visibility, setSearch, setVisibility } = useSearchParamsContext();
  const { deselectAll, selected } = useSelectionContext();

  const lockState = {
    [FieldDataType.CustomEventFields]: project?.allowAutoCustomEventFieldCreation
      ? FieldLockState.Off
      : FieldLockState.On,
    [FieldDataType.CustomEvent]: project?.extendedProjectSettings?.allowNewCustomEvents
      ? FieldLockState.Off
      : FieldLockState.On,
    [FieldDataType.User]: project?.allowAutoUserFieldCreation
      ? FieldLockState.Off
      : FieldLockState.On,
  };

  const isBulkEditMode = selected?.length > 0;
  const shouldShowBulkActions = isBulkEditMode && !!dataType && !!project.id;

  const handleChange = (val: string) => {
    const newValues = visibility.includes(val)
      ? visibility.filter((current) => current !== val)
      : [...visibility, val];
    setVisibility(newValues);
  };

  return (
    <TableActionsWrapper data-mode={shouldShowBulkActions ? 'edit' : 'view'}>
      <TableActionsRow>
        {!!fieldType && !!lockState && (
          <FieldLockStateWrapper>
            <FieldLockNotification fieldType={fieldType} fieldLockState={lockState[fieldType]} />
          </FieldLockStateWrapper>
        )}
        {!!overflowMenuOptions && (
          <Actions
            options={overflowMenuOptions}
            position={MENU_ALIGNMENTS.RIGHT}
            size={BUTTON_SIZES.LARGE}
          />
        )}
        {onCreateNewField && (
          <PrimaryButton
            data-test={TABLE_ACTIONS_TEXT.newFieldButtonTestId}
            onClick={() => onCreateNewField()}
            size={BUTTON_SIZES.LARGE}
          >
            <Add16 />
            <span>{TABLE_ACTIONS_TEXT.newFieldButtonLabel}</span>
          </PrimaryButton>
        )}
      </TableActionsRow>
      {shouldShowBulkActions ? (
        <TableBulkActions
          dataType={dataType}
          onSuccess={() => {
            deselectAll();
            refetchFields();
          }}
          projectId={project.id}
          fields={selected}
        />
      ) : (
        <TableActionsRow>
          {areFiltersEnabled && (
            <Menu
              data-test={TABLE_ACTIONS_TEXT.filtersTestId}
              options={FIELD_TYPE_FILTER_OPTIONS}
              defaultSelectedOptionIds={visibility}
              handleSelectionChange={handleChange}
              isSelectMenu
            >
              <FilterMenuTriggerButton selectedFilterCount={visibility.length} />
            </Menu>
          )}
          <SearchWrapper>
            <InputSearch
              data-test={TABLE_ACTIONS_TEXT.searchInputTestId}
              id={TABLE_ACTIONS_TEXT.searchInputTestId}
              onChange={setSearch}
              placeholder={TABLE_ACTIONS_TEXT.inputPlaceholder}
              value={search}
            />
          </SearchWrapper>
        </TableActionsRow>
      )}
    </TableActionsWrapper>
  );
};
